import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Paper, TextField, Typography } from '@mui/material';
import { ItemService } from '../../services/item.service';
import { StockService } from '../../services/stock.service';
import { ItemCustodyService } from '../../services/itemCustody.service';
import { getStoresByInstitutionId } from '../../services/store.service';
import NotInChargeNotice from '../store/NotInChargeNotice';
import { Item } from '../../interfaces/item.interface';
import { Store } from '../../interfaces/store.interface';
import { useToast } from '../../context/ToastContext';
import { TEXT_AREA_MAX_LENGTH } from '../../constants/fieldLimits';
import FabricAttributeFields, { FabricAttributes } from '../common/FabricAttributeFields';
import SearchableSelect from '../common/SearchableSelect';
import LotDateFields, { LotDates, lotDatesError, lotDatesPayload } from '../common/LotDateFields';
import { helpWhy } from '../../help/why';
import { StockInSource, STOCK_IN_SOURCE_OPTIONS } from '../model/stock-in-out';
import SerialNumbersField from '../serial/SerialNumbersField'; // F6b
import ItemScanField, { ScanOutcome } from './ItemScanField';
import StoreLabelDialog from './StoreLabelDialog';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';

const StockIn: React.FC = () => {
    const [items, setItems] = useState<Item[]>([]);
    const [stores, setStores] = useState<Store[]>([]);
    const [storesLoaded, setStoresLoaded] = useState(false);
    const [loading, setLoading] = useState(true);
    const [selectedItemId, setSelectedItemId] = useState<string>('');
    const [selectedItem, setSelectedItem] = useState<Item | null>(null);
    const [selectedStoreId, setSelectedStoreId] = useState<string>('');
    const [selectedStore, setSelectedStore] = useState<Store | null>(null);
    const [amount, setAmount] = useState<number>(0);
    const [unitCost, setUnitCost] = useState<number>(0);
    const [costTouched, setCostTouched] = useState<boolean>(false);
    const [remarks, setRemarks] = useState<string>('');
    const [source, setSource] = useState<StockInSource | ''>('');
    const [fabricAttributes, setFabricAttributes] = useState<FabricAttributes>({});
    const [lotDates, setLotDates] = useState<LotDates>({});   // F6a
    const [serials, setSerials] = useState<string[]>([]);   // F6b
    const toast = useToast();
    const [currentStock, setCurrentStock] = useState<number | null>(null);
    const [loadingStock, setLoadingStock] = useState<boolean>(false);
    const canManageItems = usePermission(PERMISSIONS.ITEM_MANAGE);
    const canPrintLabels = usePermission(PERMISSIONS.STORE_LABEL_PRINT);
    const canLabel = canManageItems || canPrintLabels;
    const [lastIn, setLastIn] = useState<{ item: Item; amount: number } | null>(null);   // the stock-in labels can be printed for
    const [labelOpen, setLabelOpen] = useState(false);

    useEffect(() => {
        fetchItems();
        fetchStores();
    }, []);

    const fetchItems = async () => {
        try {
            setLoading(true);
            const response = await ItemService.getItemsByInstitution(0, 100, '', 'STOCK'); // FW-2A
            setItems(response.content);
        } catch (err) {
            toast.showApiError(err, 'Failed to load items');
            console.error('Error fetching items:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchStores = async () => {
        try {
            setLoading(true);
            // Assuming you have a service to fetch stores
            const response = await getStoresByInstitutionId(); // Replace with actual service call
            setStores(response.content);
            setStoresLoaded(true);
        } catch (err) {
            toast.showApiError(err, 'Failed to load stores');
            console.error('Error fetching stores:', err);
        } finally {
            setLoading(false);
        }
    }

    const selectItem = (item: Item | null, startAmount: number) => {
        setSelectedItemId(item?.id ?? '');
        setSelectedItem(item);

        // Reset other fields
        setAmount(startAmount);
        // Pre-fill Unit Cost from the item's standard unit cost until the user overwrites it.
        setUnitCost(item?.standardUnitCost ?? 0);
        setCostTouched(false);
        setFabricAttributes({});
        setLotDates({});   // F6a
        setSerials([]);   // F6b
    };

    const handleItemChange = (id: string) => selectItem(items.find(item => item.id === id) || null, 0);

    // Scan-to-pick: the scanned item is chosen with amount 1; scanning it again adds 1. The item may sit past the first
    // 100 loaded into the picker, so it is added to the options.
    const handleScan = (item: Item): ScanOutcome => {
        const label = `${item.itemCode} - ${item.itemName}`;
        if ((item.itemKind ?? 'STOCK') !== 'STOCK') {
            return { ok: false, message: `${label} is not a stock item, so it cannot be stocked in.` };
        }
        if (item.id === selectedItemId) {
            const next = Number.isInteger(amount) ? amount + 1 : 1;
            setAmount(next);
            return { ok: true, message: `${label}: amount is now ${next}.` };
        }
        setItems(prev => (prev.some(it => it.id === item.id) ? prev : [...prev, item]));
        selectItem(item, 1);
        const extra = item.serialTracked ? ' Enter its serial numbers below.' : item.lotTracked ? ' Enter the unit cost and lot details below.' : '';
        return { ok: true, message: `Selected ${label}, amount 1.${extra}` };
    };

    const handleStoreChange = (id: string) => {
        setSelectedStoreId(id);

        const store = stores.find(store => store.id === id);
        setSelectedStore(store || null);
        // The amount is kept: it does not depend on the store, and a store keeper often scans first, then picks the store.
    };

    // Function to fetch current stock amount when both store and item are selected
    const fetchCurrentStock = async () => {
        if (selectedStoreId && selectedItemId) {
            try {
                setLoadingStock(true);
                setCurrentStock(null);
                const stockData = await StockService.getStockByStoreAndItem(selectedStoreId, selectedItemId);
                setCurrentStock(stockData?.availableQty ?? 0);
            } catch (err) {
                console.error('Error fetching current stock:', err);
                setCurrentStock(0); // Default to 0 if there's an error or no stock exists
            } finally {
                setLoadingStock(false);
            }
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!selectedItemId) {
            toast.showError('Please select an item');
            return;
        }

        if (amount <= 0) {
            toast.showError('Please enter a valid amount (greater than 0)');
            return;
        }

        if (!source) {
            toast.showError('Please choose a source for this stock-in');
            return;
        }

        if (selectedItem?.lotTracked && unitCost <= 0) {
            toast.showError('Please enter a valid unit cost (greater than 0) for this lot-tracked item');
            return;
        }

        const datesError = selectedItem?.lotTracked ? lotDatesError(lotDates, !!selectedItem.expiryTracked) : null;   // F6a
        if (datesError) {
            toast.showError(datesError);
            return;
        }

        // ---- F6b: one serial per unit for a serial-tracked item ----
        if (selectedItem?.serialTracked && (!Number.isInteger(amount) || serials.length !== amount)) {
            toast.showError(`Enter ${Number.isInteger(amount) ? amount : 'a whole number of'} serial numbers (one per unit)`);
            return;
        }

        try {
            const updatedStock = await StockService.stockIn({
                storeId: selectedStoreId,
                itemId: selectedItemId,
                amount: amount,
                ...(selectedItem?.lotTracked ? { unitCost, ...fabricAttributes, ...lotDatesPayload(lotDates) } : {}),
                // Non-lot items: optional cost; blank lets the server use the moving average / standard cost.
                ...(!selectedItem?.lotTracked && unitCost > 0 ? { unitCost } : {}),
                remarks: remarks || undefined,
                source,
                ...(selectedItem?.serialTracked ? { serials } : {}),   // F6b
            });

            // Update selected item with new quantity and refresh the display
            const standardCost = selectedItem?.standardUnitCost ?? 0;
            if (selectedItem) setLastIn({ item: selectedItem, amount });
            if (updatedStock) {
                setSelectedItem(prev => (updatedStock.item ? { ...prev, ...updatedStock.item } as Item : prev));
                setCurrentStock(updatedStock.availableQty);
                toast.showSuccess(`Stock in successful. New quantity: ${updatedStock?.availableQty}`);
            } else {
                toast.showSuccess('Stock in successful');
            }
            if (updatedStock?.journalWarning) {
                toast.showError(updatedStock.journalWarning);
            }

            // Reset the form
            setAmount(0);
            // Back to the item's standard cost (or blank) without flagging an error until touched.
            setUnitCost(standardCost);
            setCostTouched(false);
            setRemarks('');
            setSource('');
            setFabricAttributes({});
            setLotDates({});   // F6a
            setSerials([]);   // F6b
        } catch (error) {
            console.error('Error during stock in:', error);
            toast.showApiError(error, 'Failed to process stock in');
        }
    };

    useEffect(() => {
        fetchCurrentStock();
    }, [selectedStoreId, selectedItemId]);

    // M2: a returnable item with people still holding it in this store is usually a return, not new stock.
    const [openCustody, setOpenCustody] = useState<number>(0);
    useEffect(() => {
        setOpenCustody(0);
        if (!selectedStoreId || !selectedItemId || selectedItem?.effectiveNature !== 'RETURNABLE') return;
        let cancelled = false;
        ItemCustodyService.list({ status: 'OPEN', itemId: selectedItemId, storeId: selectedStoreId }, 0, 1)
            .then(r => { if (!cancelled) setOpenCustody(r.totalElements); })
            .catch(() => { /* a warning only: ignore errors */ });
        return () => { cancelled = true; };
    }, [selectedStoreId, selectedItemId, selectedItem]);

    if (loading) {
        return <div data-testid="loading-state">Loading...</div>;
    }

    return (
        <Box sx={{ p: 3 }} data-help-page="store.stockIn.page">
            <Typography variant="h4" sx={{ mb: 3 }}>
                Stock In
            </Typography>
            {storesLoaded && stores.length === 0 && <NotInChargeNotice />}

            <Paper elevation={3} sx={{ p: 3, maxWidth: 720 }}>
            <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }} data-help-section="store.stockIn.form">
                <Box data-help="store.stockIn.scan">
                <ItemScanField data-testid="stock-in-scan" onItem={handleScan} compact
                    label="Scan or type item barcode" />
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <SearchableSelect<Item>
                        options={items}
                        value={selectedItemId}
                        onChange={handleItemChange}
                        getOptionLabel={item => `${item.itemName} - Category: ${item.itemCategory?.fullName || item.itemCategory?.itemCategoryName}`}
                        data-help="store.stockIn.field.item" label="Select Item"
                        required
                        fullWidth={false}
                        sx={{ flex: '1 1 260px', minWidth: 0 }}
                        data-testid="item-select"
                    />
                    <SearchableSelect<Store>
                        options={stores}
                        value={selectedStoreId}
                        onChange={handleStoreChange}
                        getOptionLabel={store => store.storeName}
                        data-help="store.stockIn.field.store" label="Select Store"
                        required
                        fullWidth={false}
                        sx={{ flex: '1 1 260px', minWidth: 0 }}
                        data-testid="store-select"
                    />
                </Box>

                {(selectedItem || selectedStore) && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: -1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ flex: '1 1 260px', minWidth: 0 }} data-testid="item-details">
                            {selectedItem && <>{selectedItem.itemCategory?.fullName || selectedItem.itemCategory?.itemCategoryName}{selectedItem.description ? ` · ${selectedItem.description}` : ''}</>}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ flex: '1 1 260px', minWidth: 0 }} data-testid="store-details">
                            {selectedStore && <>{selectedStore.address || selectedStore.description}</>}
                        </Typography>
                    </Box>
                )}

                {selectedStore && selectedItem && (
                    <Alert severity="info" icon={false} data-testid="current-stock">
                        {loadingStock ? 'Loading current stock…' : (
                            <>Current stock in {selectedStore.storeName}: <strong>{currentStock ?? 0}{selectedItem?.unit ? ` ${selectedItem.unit.symbol}` : ''}</strong></>
                        )}
                    </Alert>
                )}

                {openCustody > 0 && selectedItem && (
                    <Alert severity="warning" data-testid="stock-in-open-custody" data-help="store.stockIn.warn.openCustody" sx={{ mb: 2 }}>
                        {openCustody} issue{openCustody === 1 ? ' of' : 's of'} {selectedItem.itemName} {openCustody === 1 ? 'is' : 'are'} still out with people from this store.
                        If this is one of them coming back, use Items out with people, then Return, so the holder's record is closed. Add it here only if it is new stock.
                    </Alert>
                )}

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
                    <TextField
                        data-help="store.stockIn.field.amount" label={`Stock In Amount${selectedItem?.unit ? ` (${selectedItem.unit.symbol})` : ''}`}
                        type="number"
                        value={amount}
                        onChange={(e) => {
                            const value = Number(e.target.value);
                            if (value >= 0) {
                                setAmount(value);
                            }
                        }}
                        required
                        InputProps={{
                            inputProps: { min: 0 }
                        }}
                        disabled={!selectedItem}
                        sx={{ flex: '1 1 200px', minWidth: 0 }}
                        data-testid="amount-input"
                    />

                {selectedItem && (
                    <TextField
                        data-help="store.stockIn.field.unitCost" label={selectedItem.lotTracked ? 'Unit Cost' : 'Unit Cost (optional)'}
                        type="number"
                        value={unitCost === 0 && !costTouched ? '' : unitCost}
                        onChange={(e) => {
                            const value = Number(e.target.value);
                            setCostTouched(true);
                            if (value >= 0) {
                                setUnitCost(value);
                            }
                        }}
                        required={!!selectedItem.lotTracked}
                        InputProps={{
                            inputProps: { min: 0, step: 'any' }
                        }}
                        error={!!selectedItem.lotTracked && costTouched && unitCost <= 0}
                        helperText={selectedItem.lotTracked
                            ? (costTouched && unitCost <= 0 ? 'Unit cost is required and must be greater than 0' : '')
                            : 'Leave blank to use the current average cost (or the item\'s standard cost)'}
                        sx={{ flex: '1 1 200px', minWidth: 0 }}
                        data-testid="unit-cost-input"
                    />
                )}
                </Box>

                {selectedItem?.serialTracked && (   /* F6b */
                    <Box>
                        <SerialNumbersField mode="capture" expected={amount} value={serials} onChange={setSerials}
                            itemId={selectedItemId} data-testid="stock-in-serials" />
                    </Box>
                )}

                {selectedItem?.lotTracked && (   /* F6a */
                    <Box>
                        <LotDateFields idPrefix="stock-in" value={lotDates} onChange={setLotDates}
                            expiryRequired={!!selectedItem.expiryTracked} />
                    </Box>
                )}

                {selectedItem?.lotTracked && (
                    <Box>
                        <FabricAttributeFields
                            idPrefix="stock-in"
                            value={fabricAttributes}
                            onChange={setFabricAttributes}
                        />
                    </Box>
                )}

                <TextField
                    select
                    fullWidth
                    required
                    data-help="store.stockIn.field.source" label="Source"
                    value={source}
                    onChange={(e) => setSource(e.target.value as StockInSource)}
                    disabled={!selectedItem}
                    SelectProps={{ native: true }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ 'data-testid': 'source-select' }}
                    helperText="Decides the ledger entry: returns credit the consumption account, opening stock credits Opening Balance Equity, other adjustments credit the inventory adjustment account."
                >
                    <option value="" disabled>Choose a source</option>
                    {STOCK_IN_SOURCE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </TextField>

                <TextField
                    fullWidth
                    data-help="store.stockIn.field.remarks" label="Remarks (optional)"
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    multiline
                    rows={2}
                    disabled={!selectedItem}
                    data-testid="remarks-input"
                    inputProps={{ maxLength: TEXT_AREA_MAX_LENGTH }}
                    helperText={`${remarks.length}/${TEXT_AREA_MAX_LENGTH}`}
                />

                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={!selectedItem}
                    data-help="store.stockIn.submit"
                    {...helpWhy(!selectedItem ? { kind: 'status', message: 'Choose an item first.' } : null)}
                    data-testid="submit-button"
                >
                    Stock In
                </Button>
                </Box>
            </Box>
            </Paper>

            {canLabel && lastIn && (
                <Alert severity="success" sx={{ mt: 2, maxWidth: 720 }} data-testid="stock-in-labels"
                    onClose={() => setLastIn(null)}
                    action={<Button color="inherit" size="small" data-help="store.stockIn.printLabels"
                        onClick={() => setLabelOpen(true)}>Print labels</Button>}>
                    Stocked in {lastIn.amount}{lastIn.item.unit ? ` ${lastIn.item.unit.symbol}` : ''} of {lastIn.item.itemName}. Print labels for it?
                </Alert>
            )}
            {labelOpen && lastIn && (
                <StoreLabelDialog open kind="ITEM" ids={[lastIn.item.id!]} defaultCopies={Math.ceil(lastIn.amount)}
                    onClose={() => setLabelOpen(false)} />
            )}
        </Box>
    );
};

export default StockIn;
